/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-unused-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Input from 'src/components/ui/Input/Input';
import Checkbox from 'src/components/ui/Checkbox/Checkbox';
import Divider from 'src/components/ui/Divider/Divider';
import Select, { Option } from 'src/components/ui/Select/Select';
import { SingleValue } from 'react-select';
import TablePagination from 'src/components/ui/TablePagination/TablePagination';
import { getPaginationOptions } from 'src/utils/general';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import { useTranslation } from 'react-i18next';
import useStockInventory, { StockItem } from 'src/services/inventory';
import { Product } from 'src/services/productsNew';
import styles from './SelectSingleProduct.module.scss';
import { PortalProductType } from '../SelectProducts/SelectProducts';
import Button, { ButtonToneProps } from '../../ui/Button/Button';

export interface UseSelectProductsDataResult {
  items: StockItem[] | Product[];
  pages: number;
}

export interface SelectedProductsProduct {
  id: number;
  product_id: number;
  name: string;
  barcode?: string;
  article_number?: string;
}

interface SelectSingleProductProps {
  defaultValue?: number | null;
  selectProductsData: UseSelectProductsDataResult;

  pageIndex: number;
  pageSize: number;
  searchQuery: string;

  onPaginationClick: (direction: 'previous' | 'next') => void;
  onSearchQueryChange: (searchQuery: string) => void;
  onPageSizeChange: (pageSize: number) => void;

  onConfirm: (product: SelectedProductsProduct | null) => void;
  showBadges?: boolean;
}

const SelectSingleProduct: React.FC<SelectSingleProductProps> = ({
  defaultValue,
  selectProductsData,
  pageIndex,
  pageSize,
  searchQuery,
  onPaginationClick,
  onSearchQueryChange,
  onPageSizeChange,
  onConfirm,
  showBadges,
}) => {
  const { t } = useTranslation();
  const { items, pages } = selectProductsData;
  const options = getPaginationOptions();

  const getProductId = (product: StockItem | Product): number | undefined => {
    if ('product_id' in product) return product.product_id ?? undefined;
    return product.id ?? undefined;
  };

  const normalizeProduct = (product: StockItem | Product): SelectedProductsProduct | null => {
    const productId = getProductId(product);
    if (!productId) return null;

    return {
      id: productId,
      product_id: productId,
      name: product.name,
      barcode: product.barcode ?? undefined,
      article_number: product.article_number ?? undefined,
    };
  };

  const defaultSelected = useMemo(() => {
    if (!defaultValue) return null;
    const found = items.find(p => getProductId(p) === defaultValue);
    return found ? normalizeProduct(found) : null;
  }, [defaultValue, items]);

  const [selectedProduct, setSelectedProduct] = useState<SelectedProductsProduct | null>(defaultSelected);

  useEffect(() => {
    if (defaultSelected) setSelectedProduct(defaultSelected);
  }, [defaultSelected]);

  const handleSelectProduct = (product: StockItem | Product) => {
    const normalized = normalizeProduct(product);
    if (!normalized) return;
    setSelectedProduct(normalized);
  };

  const handlePerPageUpdate = (selectedOption: SingleValue<Option>) => {
    if (selectedOption) onPageSizeChange(parseInt(selectedOption.value as string, 10));
  };

  return (
    <div className={styles.root}>
      <Input
        value={searchQuery}
        clearable
        className="mb-20"
        onChange={e => onSearchQueryChange(e.target.value)}
        placeholder={t('general.search_by_barcode_or_name')}
      />

      <div className={styles.productsContainer}>
        {items.length === 0 && (
          <div className="typo-text-12 text-faded col-span-2">{t('general.no_products_found')}</div>
        )}

        {items.map(product => {
          const productId = getProductId(product);
          const isSelected = selectedProduct?.product_id === productId;

          return (
            <div
              key={productId}
              role="button"
              tabIndex={0}
              onClick={() => handleSelectProduct(product)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') handleSelectProduct(product);
              }}
              className={classNames(styles.product, { [styles.isAdded]: isSelected })}>
              <Checkbox checked={isSelected} disabled />
              <div className="flex flex-col gap-4">
                <span className="typo-text-12--bold flex items-center gap-4">
                  {product.name}
                  {showBadges && 'on_stock' in product && product.on_stock && (
                    <Badge type={BadgeType.Neutral} expanded label={product.on_stock.toString()} />
                  )}
                  {showBadges && 'outgoing' in product && product.outgoing && (
                    <Badge type={BadgeType.Neutral2} expanded label={product.outgoing.toString()} />
                  )}
                  {showBadges && 'not_reserved' in product && product.not_reserved && (
                    <Badge type={BadgeType.Neutral3} expanded label={product.not_reserved.toString()} />
                  )}
                </span>
                <span className="typo-text-12 text-faded">
                  {product.article_number} | {product.barcode}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <Divider className="mt-20 mb-20" />

      <div className={styles.paginationContainer}>
        <div className="selectWrapper">
          <Select
            onChange={handlePerPageUpdate}
            options={options}
            defaultValue={options.find(option => option.value === pageSize.toString())}
          />
          <span className="typo-text-12 text-faded">{t('general.per_page')}</span>
        </div>
        <TablePagination maxPage={pages} onPaginationClick={onPaginationClick} currentPage={pageIndex} />
      </div>

      <div className={styles.buttonContainer}>
        <Button tone={ButtonToneProps.SECONDARY} onClick={() => onConfirm(null)} title={t('general.cancel')} />
        <Button
          tone={ButtonToneProps.PRIMARY_BLUE}
          disabled={!selectedProduct}
          onClick={() => onConfirm(selectedProduct)}
          title={t('general.confirm')}
        />
      </div>
    </div>
  );
};

export default SelectSingleProduct;

export const SelectSingleProductFromInventory: React.FC<{
  defaultValue?: number | null;
  onConfirm: (product: SelectedProductsProduct | null) => void;
  showBadges?: boolean;
  hideProduct?: number;
  excludeBomComponents?: boolean;
}> = ({ defaultValue, onConfirm, showBadges, hideProduct, excludeBomComponents = false }) => {
  const {
    stockInventory: { items, pages },
    getStockInventory,
  } = useStockInventory();

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');

  const data = useMemo(
    () => ({
      items: hideProduct
        ? items.filter(item => item.product_id !== hideProduct)
        : excludeBomComponents
          ? items.filter(item => item.portal_product_type !== PortalProductType.Set)
          : items,
      pages,
    }),
    [items, pages, hideProduct, excludeBomComponents],
  );

  return (
    <SelectSingleProduct
      defaultValue={defaultValue}
      selectProductsData={data}
      pageIndex={pageIndex}
      pageSize={pageSize}
      searchQuery={searchQuery}
      showBadges={showBadges}
      onConfirm={onConfirm}
      onSearchQueryChange={query => {
        setSearchQuery(query);
        getStockInventory(
          1,
          pageSize,
          query
            ? [
                { key: 'name', value: query, operator: 'ilike' },
                { key: 'barcode', value: query, operator: 'ilike' },
                { key: 'default_code', value: query, operator: 'ilike' },
              ]
            : undefined,
        );
      }}
      onPageSizeChange={size => {
        setPageSize(size);
        getStockInventory(1, size);
      }}
      onPaginationClick={direction => {
        const next = direction === 'previous' ? pageIndex - 1 : pageIndex + 1;
        setPageIndex(next);
        getStockInventory(next + 1, pageSize);
      }}
    />
  );
};
